import { useEffect, useState } from 'react';
import { useApi } from '@backstage/core-plugin-api';
import { catalogApiRef, useEntity } from '@backstage/plugin-catalog-react';
import {
  RELATION_HAS_PART,
  RELATION_DEPENDS_ON,
  parseEntityRef,
} from '@backstage/catalog-model';
import type { CostHistoryData } from './parseCostHistory';

const ANNOTATION_KEY = 'geniussports.com/cost-history';

function mergeCostHistories(histories: CostHistoryData[]): CostHistoryData {
  const merged: CostHistoryData = {};
  for (const history of histories) {
    for (const [month, breakdown] of Object.entries(history)) {
      if (!merged[month]) merged[month] = {};
      for (const [costType, amount] of Object.entries(breakdown)) {
        merged[month][costType] = (merged[month][costType] ?? 0) + amount;
      }
    }
  }
  return merged;
}

export interface AggregatedCostResult {
  loading: boolean;
  error?: Error;
  data: CostHistoryData | null;
  resourceCount: number;
}

export function useAggregatedCosts(): AggregatedCostResult {
  const catalogApi = useApi(catalogApiRef);
  const { entity } = useEntity();
  const [result, setResult] = useState<AggregatedCostResult>({
    loading: true,
    data: null,
    resourceCount: 0,
  });

  useEffect(() => {
    let cancelled = false;

    async function load() {
      try {
        const relations = entity.relations ?? [];
        const resourceRefs = relations
          .filter(r => {
            if (r.type !== RELATION_HAS_PART && r.type !== RELATION_DEPENDS_ON)
              return false;
            try {
              const parsed = parseEntityRef(r.targetRef);
              return parsed.kind.toLowerCase() === 'resource';
            } catch {
              return false;
            }
          })
          .map(r => r.targetRef);

        if (resourceRefs.length === 0) {
          if (!cancelled) {
            setResult({ loading: false, data: null, resourceCount: 0 });
          }
          return;
        }

        const { items } = await catalogApi.getEntitiesByRefs({
          entityRefs: resourceRefs,
          fields: ['metadata.annotations'],
        });

        if (cancelled) return;

        const histories: CostHistoryData[] = [];
        let count = 0;
        for (const item of items) {
          if (!item) continue;
          const raw = (item.metadata.annotations as Record<string, string>)?.[ANNOTATION_KEY];
          if (!raw) continue;
          try {
            const parsed = JSON.parse(raw);
            if (typeof parsed === 'object' && parsed !== null) {
              histories.push(parsed as CostHistoryData);
              count++;
            }
          } catch {
            // skip malformed
          }
        }

        const merged = histories.length > 0 ? mergeCostHistories(histories) : null;
        setResult({ loading: false, data: merged, resourceCount: count });
      } catch (err) {
        if (!cancelled) {
          setResult({
            loading: false,
            error: err instanceof Error ? err : new Error(String(err)),
            data: null,
            resourceCount: 0,
          });
        }
      }
    }

    load();
    return () => { cancelled = true; };
  }, [catalogApi, entity]);

  return result;
}
